import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useGists } from '../contexts/GistContext'
import { useTheme } from '../hooks/useTheme'
import { formatDate } from '../utils/formatDate'
import GistListSkeleton from './GistListSkeleton'
import LanguageBadge from './LanguageBadge'
import SearchInput from './SearchInput'
import ProfileSettingsModal from './ProfileSettingsModal'
import { ArrowsClockwise, Plus, Moon, Sun, User, WifiHigh, WifiSlash } from '@phosphor-icons/react'

export default function GistList() {
  const { gists, isLoading, isConnected, syncGists, forceResync } = useGists()
  const { theme, toggleTheme } = useTheme()
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  if (isLoading && gists.length === 0) {
    return <GistListSkeleton />
  }

  return (
    <div className="relative h-full flex flex-col">
      <div className="sticky top-0 z-10 p-3 border-b border-base-300 bg-base-100">
        <SearchInput />
      </div>

      <div className="flex-1 overflow-y-auto">
        {gists.length === 0 ? (
          <div className="hero min-h-full">
            <div className="hero-content text-center">
              <div className="max-w-md">
                <h2 className="text-2xl font-bold">No gists found</h2>
                <p className="py-4">Click refresh to sync your gists from GitHub</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-2">
            {gists.map((gist) => (
        <Link
          key={gist.gist_id}
          to={`/${gist.gist_id}`}
          className="block p-3 border-b border-base-300 hover:bg-base-200 transition-colors cursor-pointer"
        >
          <h3 className="text-md font-medium line-clamp-2 mb-1">
            {gist.description || 'Untitled gist'}
          </h3>

          <div className="text-xs text-base-content/60 mb-2">
            {gist.files.slice(0, 2).map(f => f.filename).join(', ')}
            {gist.files.length > 2 && ` +${gist.files.length - 2} more`}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-base-content/80">
              {formatDate(gist.gist_created_at)}
            </span>

            {Array.from(new Set(gist.files.map((f) => f.language).filter(Boolean)))
              .slice(0, 2)
              .map((lang) => (
                <LanguageBadge key={lang} language={lang} />
              ))}
          </div>
        </Link>
            ))}
          </div>
        )}
      </div>

      <div className="sticky bottom-0 left-0 right-0 bg-base-100 border-t border-base-300 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-base-content/70">
              {gists.length} gist{gists.length !== 1 ? 's' : ''}
            </span>
            {isConnected ? (
              <div className="badge badge-success badge-xs gap-1">
                <WifiHigh size={10} weight="bold" />
                <span className="hidden sm:inline">Live</span>
              </div>
            ) : (
              <div className="badge badge-error badge-xs gap-1">
                <WifiSlash size={10} weight="bold" />
                <span className="hidden sm:inline">Offline</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <a
              href="https://gist.github.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost btn-sm"
              title="New Gist"
            >
              <Plus size={20} />
            </a>
            <button
              className="btn btn-ghost btn-sm"
              onClick={toggleTheme}
              title="Toggle Theme"
            >
              {theme === 'dracula' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setIsSettingsOpen(true)}
              title="Settings"
            >
              <User size={20} />
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={syncGists}
              title="Sync Gists"
            >
              <ArrowsClockwise size={20} />
            </button>
          </div>
        </div>
      </div>

      <ProfileSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onForceResync={forceResync}
      />
    </div>
  )
}
